import React, { useLayoutEffect, useRef } from "react";
import {
  AbsoluteFill,
  cancelRender,
  continueRender,
  delayRender,
  Easing,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import type { SnapshotImage } from "../../b2c/data/brands";
import { alpha } from "../color";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Merchant photos are landscape and modest (800×450 to 1200×800). Filling
 * a 1080×1920 frame with one would mean a 4× upscale, so the video never
 * does: the sharp photo sits in a card near its native size, and the
 * frame is filled by the same photo, blurred (the blur hides the upscale).
 * Colours around the card are therefore always the photo's own.
 */

/** Card aspect for a photo: its own, kept within a sane landscape band. */
export const cardAspect = (img: SnapshotImage, min = 1.2, max = 1.78) =>
  Math.min(max, Math.max(min, img.width / Math.max(1, img.height)));

const BACKDROP_FILTER = "blur(48px) saturate(1.15)";

/**
 * The photo, `object-fit: cover`, through `BACKDROP_FILTER`. A canvas
 * filter is the same Skia blur as a CSS `filter`, with the same
 * transparent edges.
 */
const drawBackdrop = (canvas: HTMLCanvasElement, img: HTMLImageElement) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Backdrop: no 2D canvas context");
  const { width: w, height: h } = canvas;
  const s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const [sw, sh] = [w / s, h / s];
  ctx.clearRect(0, 0, w, h);
  ctx.filter = BACKDROP_FILTER;
  ctx.drawImage(
    img,
    (img.naturalWidth - sw) / 2,
    (img.naturalHeight - sh) / 2,
    sw,
    sh,
    0,
    0,
    w,
    h,
  );
};

/**
 * Full-frame blurred photo + scrim: the "room" the cards live in.
 *
 * The blur is drawn once per photo into a canvas, and only the canvas's
 * scale moves. As a CSS `filter` on the moving layer, Chrome re-blurred
 * the whole frame on every frame: about 4x the cost of the other scenes
 * in the server's software rendering.
 */
export const Backdrop: React.FC<{
  src: string;
  dim?: number;
  tint?: string;
}> = ({ src, dim = 0.42, tint = "#000000" }) => {
  const frame = useCurrentFrame();
  const { durationInFrames, width, height } = useVideoConfig();
  const canvas = useRef<HTMLCanvasElement>(null);

  useLayoutEffect(() => {
    const handle = delayRender(`Backdrop ${src}`);
    let done = false;
    const finish = () => {
      if (!done) continueRender(handle);
      done = true;
    };
    const img = new Image();
    img.src = staticFile(src);
    img.decode().then(
      () => {
        if (done) return;
        if (canvas.current) drawBackdrop(canvas.current, img);
        finish();
      },
      (error) => cancelRender(error),
    );
    return finish;
  }, [src, width, height]);

  return (
    <AbsoluteFill style={{ overflow: "hidden", background: tint }}>
      <canvas
        ref={canvas}
        width={width}
        height={height}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          // Oversized so the blur has no soft edge; a slow drift keeps the
          // room alive under still text.
          scale: interpolate(frame, [0, durationInFrames], [1.25, 1.35], clamp),
        }}
      />
      <AbsoluteFill style={{ background: alpha(tint, dim) }} />
      {/* Deeper at the top and bottom, where the text and the platform UI sit. */}
      <AbsoluteFill
        style={{
          background: `linear-gradient(180deg, ${alpha(tint, 0.3)} 0%, transparent 35%, transparent 65%, ${alpha(tint, 0.3)} 100%)`,
        }}
      />
    </AbsoluteFill>
  );
};

type CardProps = {
  img: SnapshotImage;
  /** Centre of the card, stage px. */
  x: number;
  y: number;
  width: number;
  /** Defaults to the photo's own (clamped) aspect. */
  height?: number;
  /** Local frame the card lands on (it flies in over the 10 frames before). */
  at?: number;
  rotate?: number;
  /** Where it flies in from, in card heights (0 1 = from below). */
  from?: [number, number];
  radius?: number;
  /** Ken Burns zoom over the scene: [start, end]. */
  zoom?: [number, number];
  /** object-position of the crop. */
  focus?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

/** A photo print: rounded, white-edged, dropped on the beat, slowly zooming. */
export const PhotoCard: React.FC<CardProps> = ({
  img,
  x,
  y,
  width,
  height,
  at = 0,
  rotate = 0,
  from = [0, 1.4],
  radius = 36,
  zoom = [1.04, 1.14],
  focus = "50% 50%",
  style,
  children,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const h = height ?? width / cardAspect(img);
  const land = spring({
    frame: frame - at + 10,
    fps,
    config: { damping: 18, stiffness: 150, mass: 0.8 },
  });
  if (frame < at - 10) return null;
  const border = Math.round(width * 0.014);
  return (
    <div
      style={{
        position: "absolute",
        left: x - width / 2,
        top: y - h / 2,
        width,
        height: h,
        borderRadius: radius,
        padding: border,
        background: "#ffffff",
        boxShadow:
          "0 40px 90px -24px rgba(0,0,0,0.55), 0 10px 24px -8px rgba(0,0,0,0.25)",
        translate: `${(1 - land) * from[0] * width}px ${(1 - land) * from[1] * h}px`,
        rotate: `${rotate + (1 - land) * rotate * 2}deg`,
        ...style,
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: radius - border,
          overflow: "hidden",
        }}
      >
        <Img
          src={staticFile(img.src)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: focus,
            scale: interpolate(frame, [at - 10, durationInFrames], zoom, {
              ...clamp,
              easing: Easing.out(Easing.quad),
            }),
          }}
        />
        {children}
      </div>
    </div>
  );
};
